import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api/client';
import Code from '../components/Code';
import HistoryCompoundingPanel from '../components/evolution/HistoryCompoundingPanel';
import { C2_EVOLUTION_ENABLED } from '../evolutionFlag';
import type { DecisionRow, LearningRecalibration, RecalibrationType } from '../types';

const RECALIBRATION_LABELS: Record<RecalibrationType, string> = {
  scoring_weight: 'Scoring weights',
  tripwire_threshold: 'Tripwire thresholds',
  assumption_update: 'Assumption updates',
  intervention_retrain: 'Intervention retrains',
  solver_calibration: 'Solver calibrations',
};

const RECALIBRATION_ORDER: RecalibrationType[] = [
  'scoring_weight',
  'tripwire_threshold',
  'assumption_update',
  'intervention_retrain',
  'solver_calibration',
];

// Macro band: the slow, long-horizon (3-5 year) spine-learning view, PRESERVED verbatim from
// the original Learning Loop screen -- same /api/learning data path, same sections. Only the
// page header below is reframed to label it as the macro band; the per-intervention fast grid
// is a separate, independently-failable component (LearningGrid) rendered beneath it.
export default function LearningMacro() {
  const learning = useQuery({ queryKey: ['learning'], queryFn: api.learning });

  if (learning.isLoading) return <div className="small faint">Loading macro learning band...</div>;
  if (learning.isError) return <div className="small faint">Macro learning band unavailable.</div>;

  const l = learning.data!;
  const openDecisions = l.decisions.filter((d) => d.actual_outcome === null);
  const closedDecisions = l.decisions.filter((d) => d.actual_outcome !== null);
  const recalibrationsByType = groupRecalibrations(l.recalibrations);
  const trendData = l.accuracy_trend.map((point) => ({
    ...point,
    calibration_error_pct: point.calibration_error * 100,
    mean_abs_pct_error_pct: point.mean_abs_pct_error * 100,
  }));

  return (
    <div>
      <div className="ph">Macro band · long-horizon (3–5 year) spine learning</div>
      <div className="h2">Predicted vs actual - the spine tunes itself</div>
      <p className="lead">
        The slow loop: scoring weights, calibration, and tripwire precision recalibrate as decisions
        close over quarters and years. Plain business terms — expected outcome, actual, variance, and
        the recalibration it caused. (The fast, per-intervention loop is below.)
      </p>
      {C2_EVOLUTION_ENABLED && <HistoryCompoundingPanel />}

      <section id="learning-predicted-vs-actual" className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Predicted vs actual</div>

        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div className="small">
            {openDecisions.length} open decision{openDecisions.length === 1 ? '' : 's'} · {closedDecisions.length} closed decision{closedDecisions.length === 1 ? '' : 's'}
          </div>
          <span className="tag muted">no raw payloads</span>
        </div>

        {openDecisions.length > 0 && (
          <>
            <div className="qhead" style={{ marginTop: 12, marginBottom: 8 }}>
              <span className="qdot" style={{ background: 'var(--amber)' }} />Open decisions awaiting actuals
            </div>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Signal</th>
                  <th>Disposition</th>
                  <th>Owner</th>
                  <th>Prediction</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {openDecisions.map((decision) => (
                  <tr key={decision.id}>
                    <td className="mono" style={{ color: 'var(--faint)' }}>{decision.id}</td>
                    <td><span className="tag amber"><Code id={decision.signal_id} kind="signal" compact /></span></td>
                    <td><span className="tag muted">{formatDisposition(decision.disposition)}</span></td>
                    <td>{decision.owner}</td>
                    <td>{decision.prediction_summary}</td>
                    <td><span className="tag gold">{decision.gap_summary}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="qhead" style={{ marginTop: openDecisions.length > 0 ? 18 : 8, marginBottom: 8 }}>
          <span className="qdot" style={{ background: 'var(--olive)' }} />Closed decisions
        </div>
        {closedDecisions.length === 0 ? (
          <div className="small">No actuals have been recorded yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Signal</th>
                <th>Disposition</th>
                <th>Predicted</th>
                <th>Actual</th>
                <th>Gap</th>
              </tr>
            </thead>
            <tbody>
              {closedDecisions.map((decision) => (
                <tr key={decision.id}>
                  <td className="mono" style={{ color: 'var(--faint)' }}>{decision.id}</td>
                  <td><span className="tag amber"><Code id={decision.signal_id} kind="signal" compact /></span></td>
                  <td><span className="tag muted">{formatDisposition(decision.disposition)}</span></td>
                  <td>{decision.prediction_summary}</td>
                  <td>{decision.actual_summary}</td>
                  <td style={{ color: gapColor(decision), fontWeight: 700 }}>{decision.gap_summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section id="learning-recalibrations" className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />What changed</div>
        {RECALIBRATION_ORDER.map((type) => {
          const rows = recalibrationsByType[type] ?? [];
          if (rows.length === 0) return null;
          return (
            <div key={type} style={{ marginTop: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span className="tag teal">{RECALIBRATION_LABELS[type]}</span>
                <span className="small">{rows.length} recalibration{rows.length === 1 ? '' : 's'}</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Target</th>
                    <th>Before {'->'} after</th>
                    <th>Source gap</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div>{row.target}</div>
                        <div className="tiny">{formatDate(row.ts)}{row.source_decision_id ? ` · ${row.source_decision_id}` : ''}</div>
                      </td>
                      <td className="mono small">{row.before}{' -> '}{row.after}</td>
                      <td>{row.gap_summary}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </section>

      <section id="learning-levels" className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Three levels of learning</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <LearningLevelBlock
            label="Strategic spine"
            count={l.learning_levels.strategic_spine.count}
            accent="var(--amber)"
            primary={`Last re-weighted ${formatDate(l.learning_levels.strategic_spine.last_reweighted)}`}
            secondary={l.learning_levels.strategic_spine.latest_recalibration?.reason ?? 'No scoring-weight recalibration yet.'}
          />
          <LearningLevelBlock
            label="Self-learning interventions"
            count={l.learning_levels.self_learning_interventions.count}
            accent="var(--blue)"
            primary={`Last retrained ${l.learning_levels.self_learning_interventions.last_retrain ?? 'n/a'}`}
            secondary={`${l.learning_levels.self_learning_interventions.trend ?? 'trend pending'} · ${sampleItems(l.learning_levels.self_learning_interventions.items)}`}
          />
          <LearningLevelBlock
            label="Parameter-calibrated solvers"
            count={l.learning_levels.parameter_calibrated_solvers.count}
            accent="var(--teal)"
            primary={`Last calibrated ${l.learning_levels.parameter_calibrated_solvers.last_calibration ?? 'n/a'}`}
            secondary={`${l.learning_levels.parameter_calibrated_solvers.data_window ?? 'window pending'} · ${sampleItems(l.learning_levels.parameter_calibrated_solvers.items)}`}
          />
          <LearningLevelBlock
            label="Static interventions"
            count={l.learning_levels.static_count}
            accent="var(--faint)"
            primary="No retraining or calibration"
            secondary="Static checks stay labeled honestly instead of being described as learning systems."
          />
        </div>
      </section>

      <section id="learning-accuracy-trend" className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--olive)' }} />Accuracy over time</div>
        <div className="small" style={{ marginBottom: 10 }}>Prediction accuracy rises while calibration error falls.</div>
        <div style={{ height: 260, minWidth: 0 }}>
          <ResponsiveContainer>
            <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" />
              <XAxis dataKey="period" stroke="var(--faint)" tick={{ fill: 'var(--faint)', fontSize: 12 }} />
              <YAxis
                stroke="var(--faint)"
                tick={{ fill: 'var(--faint)', fontSize: 12 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{ background: 'var(--panel)', border: '1px solid var(--line)', color: 'var(--ink)' }}
                formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
              />
              <Legend />
              <Line type="monotone" dataKey="accuracy_pct" name="Prediction accuracy" stroke="var(--olive)" strokeWidth={3} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="calibration_error_pct" name="Calibration error" stroke="var(--red)" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Period</th>
              <th align="right">Closed decisions</th>
              <th align="right">Mean abs. error</th>
              <th align="right">Prediction accuracy</th>
              <th align="right">Calibration error</th>
            </tr>
          </thead>
          <tbody>
            {trendData.map((point) => (
              <tr key={point.period}>
                <td className="mono">{point.period}</td>
                <td align="right">{point.closed_decisions}</td>
                <td align="right">{formatPct(point.mean_abs_pct_error_pct)}</td>
                <td align="right" style={{ color: 'var(--olive)', fontWeight: 700 }}>{formatPct(point.accuracy_pct)}</td>
                <td align="right" style={{ color: 'var(--red)', fontWeight: 700 }}>{formatPct(point.calibration_error_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function LearningLevelBlock({
  label,
  count,
  accent,
  primary,
  secondary,
}: {
  label: string;
  count: number;
  accent: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div style={{ border: '1px solid var(--line)', borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: 14, background: 'var(--panel2)' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 700 }}>{label}</div>
        <span className="tag muted">{count}</span>
      </div>
      <div className="small" style={{ marginTop: 8, color: 'var(--ink)' }}>{primary}</div>
      <div className="small" style={{ marginTop: 4 }}>{secondary}</div>
    </div>
  );
}

function groupRecalibrations(rows: LearningRecalibration[]) {
  return rows.reduce<Record<RecalibrationType, LearningRecalibration[]>>((acc, row) => {
    acc[row.type] = [...(acc[row.type] ?? []), row];
    return acc;
  }, {
    scoring_weight: [],
    tripwire_threshold: [],
    assumption_update: [],
    intervention_retrain: [],
    solver_calibration: [],
  });
}

function gapColor(decision: DecisionRow) {
  const direction = String(decision.predicted_vs_actual?.direction ?? '');
  if (direction === 'beat') return 'var(--olive)';
  if (direction === 'miss') return 'var(--red)';
  return 'var(--muted)';
}

function formatDate(value?: string | null) {
  if (!value) return 'n/a';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDisposition(value: string) {
  return value.replaceAll('_', ' ');
}

function formatPct(value: number) {
  return `${value.toFixed(1)}%`;
}

function sampleItems(items: { name: string }[]) {
  if (items.length === 0) return 'items listed in engine catalog';
  const sample = items.slice(0, 3).map((item) => item.name).join(', ');
  const remaining = items.length - 3;
  return remaining > 0 ? `${sample} + ${remaining} more` : sample;
}
